import React, { useState } from 'react';
import { ChevronDown, ChevronRight, X, Bot, User, FileText, MessageSquare, Cpu, Search, Code, Reply, Zap, List, BookOpen, ArrowRight } from 'lucide-react';
import { TOOL_ICONS, TOOL_COLORS } from '../utils/toolConstants';
import { useLanguage } from '../i18n';
import Modal from './ui/Modal';

const formatTokens = (n) => {
    if (n == null) return '?';
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toString();
};

// Section labels are resolved at render time (via labelKey/labelFallback) since
// this array is module-level and cannot call the t() hook.
const SECTIONS = [
    { key: 'system_prompt', labelKey: 'msgCtxSectionSystemPrompt', labelFallback: 'System Prompt', color: 'bg-blue-500', borderColor: 'border-blue-500', badgeBg: 'bg-blue-100 dark:bg-blue-900/40', badgeText: 'text-blue-700 dark:text-blue-300', Icon: Cpu },
    { key: 'workspace_context', labelKey: 'msgCtxSectionWorkspaceContext', labelFallback: 'Workspace Context', color: 'bg-emerald-500', borderColor: 'border-emerald-500', badgeBg: 'bg-emerald-100 dark:bg-emerald-900/40', badgeText: 'text-emerald-700 dark:text-emerald-300', Icon: Code },
    { key: 'conversation_history', labelKey: 'msgCtxSectionConversationHistory', labelFallback: 'Conversation History', color: 'bg-amber-500', borderColor: 'border-amber-500', badgeBg: 'bg-amber-100 dark:bg-amber-900/40', badgeText: 'text-amber-700 dark:text-amber-300', Icon: MessageSquare },
    { key: 'replied_messages', labelKey: 'msgCtxSectionRepliedMessages', labelFallback: 'Replied Messages', color: 'bg-teal-500', borderColor: 'border-teal-500', badgeBg: 'bg-teal-100 dark:bg-teal-900/40', badgeText: 'text-teal-700 dark:text-teal-300', Icon: Reply },
    { key: 'document_context', labelKey: 'msgCtxSectionDocumentContext', labelFallback: 'Document Context', color: 'bg-purple-500', borderColor: 'border-purple-500', badgeBg: 'bg-purple-100 dark:bg-purple-900/40', badgeText: 'text-purple-700 dark:text-purple-300', Icon: FileText },
    { key: 'current_query', labelKey: 'msgCtxSectionCurrentQuery', labelFallback: 'Current Query', color: 'bg-orange-500', borderColor: 'border-orange-500', badgeBg: 'bg-orange-100 dark:bg-orange-900/40', badgeText: 'text-orange-700 dark:text-orange-300', Icon: Search },
];

const TruncatedContent = ({ content, maxLength = 500, className = '' }) => {
    const { t } = useLanguage();
    const [expanded, setExpanded] = useState(false);

    if (!content) return <span className="text-pai-faint italic text-xs">{t('msgCtxEmpty', '(empty)')}</span>;

    const needsTruncation = content.length > maxLength;
    const displayContent = needsTruncation && !expanded ? content.slice(0, maxLength) + '...' : content;

    return (
        <div>
            <pre className={`text-xs font-mono whitespace-pre-wrap break-words text-white text-pai-secondary ${className}`}>
                {displayContent}
            </pre>
            {needsTruncation && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                    {expanded ? t('msgCtxShowLess', 'Show less') : t('msgCtxShowMore', 'Show more')}
                </button>
            )}
        </div>
    );
};

const SectionHeader = ({ section, tokens, isExpanded, onToggle, subtitle }) => {
    const { t } = useLanguage();
    const { labelKey, labelFallback, borderColor, badgeBg, badgeText, Icon } = section;
    return (
        <button
            onClick={onToggle}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.07] transition-colors border-l-4 ${borderColor}`}
        >
            {isExpanded ? <ChevronDown size={14} className="text-pai-meta shrink-0" /> : <ChevronRight size={14} className="text-pai-meta shrink-0" />}
            <Icon size={14} className="text-pai-secondary shrink-0" />
            <span className="text-sm font-medium text-white flex-1">
                {t(labelKey, labelFallback)}
                {subtitle && <span className="ml-2 text-xs font-normal text-pai-meta">{subtitle}</span>}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${badgeBg} ${badgeText} font-medium shrink-0`}>
                {t('msgCtxTokensCount', '{n} tokens', { n: formatTokens(tokens) })}
            </span>
        </button>
    );
};

const SystemPromptSection = ({ data }) => {
    const { t } = useLanguage();
    if (!data?.content) return <div className="px-4 py-2 text-xs text-pai-faint italic">{t('msgCtxNotAvailable', '(not available)')}</div>;
    return (
        <div className="px-4 py-2 bg-white/[0.025]">
            <TruncatedContent content={data.content} maxLength={500} />
        </div>
    );
};

const WorkspaceContextSection = ({ data }) => {
    const { t } = useLanguage();
    if (!data) return <div className="px-4 py-2 text-xs text-pai-faint italic">{t('msgCtxNotConfigured', '(not configured)')}</div>;

    const soul = data.soul;
    const user = data.user;

    return (
        <div className="px-4 py-2 space-y-3 bg-white/[0.025]">
            <div>
                <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-1 uppercase tracking-wide">SOUL</div>
                {soul?.content ? (
                    <TruncatedContent content={soul.content} maxLength={500} />
                ) : (
                    <span className="text-xs text-pai-faint italic">{t('msgCtxNotConfigured', '(not configured)')}</span>
                )}
            </div>
            <div>
                <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-1 uppercase tracking-wide">USER</div>
                {user?.content ? (
                    <TruncatedContent content={user.content} maxLength={500} />
                ) : (
                    <span className="text-xs text-pai-faint italic">{t('msgCtxNotConfigured', '(not configured)')}</span>
                )}
            </div>
        </div>
    );
};

const ConversationHistorySection = ({ data }) => {
    const { t } = useLanguage();
    if (!data || data.length === 0) return <div className="px-4 py-2 text-xs text-pai-faint italic">{t('msgCtxNoHistory', '(no history)')}</div>;

    return (
        <div className="px-4 py-2 space-y-2 bg-white/[0.025]">
            {data.map((msg, idx) => (
                <div key={idx} className="flex gap-2 p-2 bg-white/[0.03] rounded border border-pai-subtle">
                    <div className="shrink-0 mt-0.5">
                        {msg.role === 'user' ? (
                            <User size={14} className="text-blue-500" />
                        ) : (
                            <Bot size={14} className="text-purple-500" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-pai-secondary capitalize">{msg.role}</span>
                            {msg.token_estimate != null && (
                                <span className="text-xs text-pai-faint">~{t('msgCtxTokensCount', '{n} tokens', { n: formatTokens(msg.token_estimate) })}</span>
                            )}
                        </div>
                        <TruncatedContent content={msg.content} maxLength={300} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const RepliedMessageSection = ({ data }) => {
    const { t } = useLanguage();
    // Support both legacy single object and new array format
    const items = Array.isArray(data) ? data : (data?.content ? [data] : []);
    if (items.length === 0) return <div className="px-4 py-2 text-xs text-pai-faint italic">{t('msgCtxNotAvailable', '(not available)')}</div>;
    return (
        <div className="px-4 py-2 space-y-2 bg-white/[0.025]">
            {items.map((msg, idx) => (
                <div key={idx} className="flex gap-2 p-2 bg-white/[0.03] rounded border border-pai-subtle">
                    <div className="shrink-0 mt-0.5">
                        {msg.role === 'user' ? (
                            <User size={14} className="text-blue-500" />
                        ) : (
                            <Bot size={14} className="text-purple-500" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-pai-secondary capitalize">
                                {msg.role === 'user' ? t('msgCtxRoleUser', 'User') : t('msgCtxRoleAssistant', 'Assistant')}
                            </span>
                            {msg.token_estimate != null && (
                                <span className="text-xs text-pai-faint">~{t('msgCtxTokensCount', '{n} tokens', { n: formatTokens(msg.token_estimate) })}</span>
                            )}
                        </div>
                        <TruncatedContent content={msg.content} maxLength={400} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const DocumentContextSection = ({ data }) => {
    const { t } = useLanguage();
    if (!data || data.length === 0) return <div className="px-4 py-2 text-xs text-pai-faint italic">{t('msgCtxNoDocuments', '(no documents)')}</div>;

    return (
        <div className="px-4 py-2 space-y-2 bg-white/[0.025]">
            {data.map((doc, idx) => (
                <div key={idx} className="p-2 bg-white/[0.03] rounded border border-pai-subtle">
                    <div className="flex items-center gap-2 mb-1">
                        <FileText size={14} className="text-purple-500 shrink-0" />
                        <span className="text-xs font-semibold text-white truncate">{doc.name || t('msgCtxUnnamedDocument', 'Unnamed document')}</span>
                        {doc.token_estimate != null && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 shrink-0">
                                {t('msgCtxTokensCount', '{n} tokens', { n: formatTokens(doc.token_estimate) })}
                            </span>
                        )}
                    </div>
                    <TruncatedContent content={doc.content} maxLength={200} />
                </div>
            ))}
        </div>
    );
};

const CurrentQuerySection = ({ data }) => {
    const { t } = useLanguage();
    if (!data?.content) return <div className="px-4 py-2 text-xs text-pai-faint italic">{t('msgCtxNotAvailable', '(not available)')}</div>;
    return (
        <div className="px-4 py-2 bg-white/[0.025]">
            <pre className="text-xs font-mono whitespace-pre-wrap break-words text-white text-pai-secondary">
                {data.content}
            </pre>
        </div>
    );
};

const TokenSummaryBar = ({ promptBreakdown }) => {
    const { t } = useLanguage();
    const total = promptBreakdown.total_token_estimate || 1;

    const segments = [
        { key: 'system_prompt', tokens: promptBreakdown.system_prompt?.token_estimate || 0, color: 'bg-blue-500', label: t('msgCtxLegendSystem', 'System'), textColor: 'text-blue-600 dark:text-blue-400' },
        { key: 'workspace_context', tokens: promptBreakdown.workspace_context?.total_token_estimate || 0, color: 'bg-emerald-500', label: t('msgCtxLegendWorkspace', 'Workspace'), textColor: 'text-emerald-600 dark:text-emerald-400' },
        { key: 'conversation_history', tokens: promptBreakdown.conversation_history_token_estimate || 0, color: 'bg-amber-500', label: t('msgCtxLegendHistory', 'History'), textColor: 'text-amber-600 dark:text-amber-400' },
        { key: 'replied_messages', tokens: promptBreakdown.replied_messages_token_estimate || (promptBreakdown.replied_message?.token_estimate) || 0, color: 'bg-teal-500', label: t('msgCtxLegendReply', 'Reply'), textColor: 'text-teal-600 dark:text-teal-400' },
        { key: 'document_context', tokens: promptBreakdown.document_context_token_estimate || 0, color: 'bg-purple-500', label: t('msgCtxLegendDocuments', 'Documents'), textColor: 'text-purple-600 dark:text-purple-400' },
        { key: 'current_query', tokens: promptBreakdown.current_query?.token_estimate || 0, color: 'bg-orange-500', label: t('msgCtxLegendQuery', 'Query'), textColor: 'text-orange-600 dark:text-orange-400' },
    ].filter(s => s.tokens > 0);

    return (
        <div className="mb-4">
            {/* Bar */}
            <div className="h-3 flex rounded-full overflow-hidden bg-white/[0.07]">
                {segments.map((seg) => {
                    const pct = Math.max((seg.tokens / total) * 100, 1);
                    return (
                        <div
                            key={seg.key}
                            className={`${seg.color} transition-all`}
                            style={{ width: `${pct}%` }}
                            title={`${seg.label}: ${t('msgCtxTokensCount', '{n} tokens', { n: formatTokens(seg.tokens) })}`}
                        />
                    );
                })}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {segments.map((seg) => (
                    <div key={seg.key} className="flex items-center gap-1.5 text-xs">
                        <div className={`w-2.5 h-2.5 rounded-sm ${seg.color}`} />
                        <span className={`${seg.textColor} font-medium`}>{seg.label}</span>
                        <span className="text-pai-meta">{formatTokens(seg.tokens)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Agentic Trace Visualization ─────────────────────────────────────────────

// TOOL_ICONS and TOOL_COLORS imported from ../utils/toolConstants

const STEP_STYLES = {
    system:   { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500', Icon: Cpu },
    history:  { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500', Icon: MessageSquare },
    reply:    { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-700 dark:text-teal-300', dot: 'bg-teal-500', Icon: Reply },
    query:    { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500', Icon: Search },
    response: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', dot: 'bg-green-500', Icon: Bot },
};

const AgenticTraceView = ({ trace, iterations, toolCallCount }) => {
    const { t } = useLanguage();
    const [expandedSteps, setExpandedSteps] = useState(new Set());

    const toggleStep = (idx) => {
        setExpandedSteps(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    if (!trace || trace.length === 0) return null;

    return (
        <div>
            {/* Summary bar */}
            <div className="mb-4 flex items-center gap-3 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-semibold flex items-center gap-1.5">
                    <Zap size={12} /> {t('msgCtxAgenticPipeline', 'Agentic Pipeline')}
                </span>
                <span className="text-xs text-pai-meta">
                    {t('msgCtxIterationsLabel', '{n} iteration{s}', { n: iterations, s: iterations !== 1 ? 's' : '' })} &middot; {t('msgCtxToolCallsLabel', '{n} tool call{s}', { n: toolCallCount, s: toolCallCount !== 1 ? 's' : '' })}
                </span>
            </div>

            {/* Timeline */}
            <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-white/[0.07]" />

                <div className="space-y-1">
                    {trace.map((step, idx) => {
                        const isToolCall = step.type === 'tool_call';
                        const style = isToolCall
                            ? (TOOL_COLORS[step.tool] || TOOL_COLORS.search_documents)
                            : (STEP_STYLES[step.type] || STEP_STYLES.query);
                        const IconComponent = isToolCall
                            ? (TOOL_ICONS[step.tool] || Search)
                            : (style.Icon || Cpu);
                        const isExpanded = expandedSteps.has(idx);
                        const hasExpandableContent = isToolCall && step.args;

                        return (
                            <div key={idx} className="relative flex items-start gap-3 pl-0">
                                {/* Timeline dot */}
                                <div className={`relative z-10 mt-2.5 w-[9px] h-[9px] rounded-full ring-2 ring-white  ${style.dot} shrink-0 ml-[11px]`} />

                                {/* Step card */}
                                <div
                                    className={`flex-1 rounded-lg border ${isToolCall ? style.border : 'border-pai-subtle'} ${style.bg} px-3 py-2 ${hasExpandableContent ? 'cursor-pointer' : ''}`}
                                    onClick={() => hasExpandableContent && toggleStep(idx)}
                                >
                                    <div className="flex items-center gap-2">
                                        <IconComponent size={14} className={style.text} />
                                        <span className={`text-xs font-semibold ${style.text}`}>
                                            {step.label}
                                        </span>
                                        {isToolCall && step.iteration && (
                                            <span className="text-xs text-pai-faint">
                                                {t('msgCtxStepLabel', 'step {n}', { n: step.iteration })}
                                            </span>
                                        )}
                                        {hasExpandableContent && (
                                            <span className="ml-auto text-pai-faint">
                                                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-pai-meta mt-0.5 ml-[22px]">
                                        {step.detail}
                                    </div>

                                    {/* Expanded args */}
                                    {isExpanded && step.args && (
                                        <pre className="mt-2 ml-[22px] text-xs font-mono bg-white/[0.05] rounded p-2 text-pai-secondary whitespace-pre-wrap break-all">
                                            {JSON.stringify(step.args, null, 2)}
                                        </pre>
                                    )}
                                    {/* Web search result preview */}
                                    {isExpanded && step.result_preview && (
                                        <pre className="mt-2 ml-[22px] text-xs font-mono bg-cyan-50/50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded p-2 text-pai-secondary whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                                            {step.result_preview}
                                        </pre>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

const ContextViewerModal = ({ promptBreakdown, fullPrompt, isLoading, error, onClose, groundingEnabled, groundingUsed, groundingSearchCount, retentionExpired = false }) => {
    const { t } = useLanguage();
    const [expandedSections, setExpandedSections] = useState(new Set(['current_query']));
    const [showRaw, setShowRaw] = useState(false);

    const toggleSection = (key) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const getTokensForSection = (key) => {
        if (!promptBreakdown) return null;
        switch (key) {
            case 'system_prompt':
                return promptBreakdown.system_prompt?.token_estimate;
            case 'workspace_context':
                return promptBreakdown.workspace_context?.total_token_estimate;
            case 'conversation_history':
                return promptBreakdown.conversation_history_token_estimate;
            case 'replied_messages':
                return promptBreakdown.replied_messages_token_estimate || promptBreakdown.replied_message?.token_estimate;
            case 'document_context':
                return promptBreakdown.document_context_token_estimate;
            case 'current_query':
                return promptBreakdown.current_query?.token_estimate;
            default:
                return null;
        }
    };

    const getSectionSubtitle = (key) => {
        if (!promptBreakdown) return null;
        switch (key) {
            case 'conversation_history': {
                const history = promptBreakdown.conversation_history;
                const count = history?.length || promptBreakdown.message_count || 0;
                const tokens = promptBreakdown.conversation_history_token_estimate;
                return count > 0 ? `(${count} messages, ~${formatTokens(tokens)} tokens)` : null;
            }
            case 'replied_messages': {
                const msgs = promptBreakdown.replied_messages || (promptBreakdown.replied_message ? [promptBreakdown.replied_message] : []);
                if (msgs.length === 0) return null;
                if (msgs.length === 1) return `(replying to ${msgs[0].role})`;
                return `(${msgs.length} messages)`;
            }
            case 'document_context': {
                const docs = promptBreakdown.document_context;
                const count = docs?.length || 0;
                const tokens = promptBreakdown.document_context_token_estimate;
                return count > 0 ? `(${count} document${count !== 1 ? 's' : ''}, ~${formatTokens(tokens)} tokens)` : null;
            }
            default:
                return null;
        }
    };

    const renderSectionContent = (key) => {
        if (!promptBreakdown) return null;
        switch (key) {
            case 'system_prompt':
                return <SystemPromptSection data={promptBreakdown.system_prompt} />;
            case 'workspace_context':
                return <WorkspaceContextSection data={promptBreakdown.workspace_context} />;
            case 'conversation_history':
                return <ConversationHistorySection data={promptBreakdown.conversation_history} tokenEstimate={promptBreakdown.conversation_history_token_estimate} />;
            case 'replied_messages':
                return <RepliedMessageSection data={promptBreakdown.replied_messages || promptBreakdown.replied_message} />;
            case 'document_context':
                return <DocumentContextSection data={promptBreakdown.document_context} tokenEstimate={promptBreakdown.document_context_token_estimate} />;
            case 'current_query':
                return <CurrentQuerySection data={promptBreakdown.current_query} />;
            default:
                return null;
        }
    };

    // Filter out optional sections when they have no data
    const visibleSections = SECTIONS.filter((s) => {
        if (!promptBreakdown) return true;
        if (s.key === 'replied_messages') {
            return (promptBreakdown.replied_messages && promptBreakdown.replied_messages.length > 0) || promptBreakdown.replied_message != null;
        }
        if (s.key === 'document_context') {
            const docs = promptBreakdown.document_context;
            return docs && docs.length > 0;
        }
        return true;
    });

    const pipelineLabel = promptBreakdown?.pipeline
        ? promptBreakdown.pipeline.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        : null;

    const hasStructuredView = promptBreakdown != null;

    return (
        <Modal onClose={onClose} size="none" layout="column" cardClassName="w-full max-w-4xl max-h-[85dvh]">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-pai-subtle">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-white">
                            {t('msgCtxHeaderTitle', 'LLM Context')}
                        </h3>
                        {pipelineLabel && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.07] text-pai-secondary font-medium uppercase tracking-wide">
                                {pipelineLabel}
                            </span>
                        )}
                        {promptBreakdown?.total_token_estimate != null && (
                            <span className="text-xs text-pai-meta">
                                ~{t('msgCtxTotalTokens', '{n} total tokens', { n: formatTokens(promptBreakdown.total_token_estimate) })}
                            </span>
                        )}
                        {groundingUsed && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 font-medium flex items-center gap-1">
                                {t('msgCtxGroundingUsed', '🌐 Grounding Used')}{groundingSearchCount > 0 && ` (${groundingSearchCount})`}
                            </span>
                        )}
                        {groundingEnabled && !groundingUsed && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.06] text-pai-meta font-medium">
                                {t('msgCtxGroundingOffered', '🌐 Grounding Offered')}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {hasStructuredView && fullPrompt && (
                            <button
                                onClick={() => setShowRaw(!showRaw)}
                                className="text-xs px-2 py-1 rounded border border-pai text-pai-secondary hover:bg-white/[0.07] transition-colors"
                            >
                                {showRaw ? t('msgCtxShowStructuredView', 'Show Structured View') : t('msgCtxShowRawPrompt', 'Show Raw Prompt')}
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="text-pai-meta hover:text-white  p-1 rounded hover:bg-white/[0.07] transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                            <span className="ml-3 text-pai-meta">{t('msgCtxLoadingContext', 'Loading context...')}</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <div className="text-red-500 dark:text-red-400 mb-2">{t('msgCtxFailedToLoad', 'Failed to load context')}</div>
                            <div className="text-sm text-pai-meta">{error}</div>
                        </div>
                    ) : retentionExpired && !fullPrompt && !promptBreakdown ? (
                        // #460: the technical prompt copy is removed after 90
                        // days by the retention sweep — say so honestly rather
                        // than "no context", which reads like a bug.
                        <div data-testid="context-viewer-expired" className="text-center py-12 text-pai-meta">
                            {t('msgCtxRetentionExpired', 'Technical details are kept for 90 days.')}
                        </div>
                    ) : showRaw || !hasStructuredView ? (
                        // Raw / fallback view
                        fullPrompt ? (
                            <div className="bg-white/[0.025] rounded border border-pai-subtle p-4">
                                <pre className="text-xs font-mono whitespace-pre-wrap break-words text-white">
                                    {fullPrompt}
                                </pre>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-pai-meta">
                                {t('msgCtxNoContextAvailable', 'No context available for this message')}
                            </div>
                        )
                    ) : promptBreakdown?.pipeline === 'agentic' ? (
                        // Agentic trace view — shows the tool-use loop timeline
                        <AgenticTraceView
                            trace={promptBreakdown.agentic_trace}
                            iterations={promptBreakdown.agentic_iterations || 0}
                            toolCallCount={promptBreakdown.agentic_tool_calls || 0}
                        />
                    ) : (
                        // Structured view (waterfall pipeline)
                        <div>
                            <TokenSummaryBar promptBreakdown={promptBreakdown} />

                            <div className="rounded-lg border border-pai-subtle overflow-hidden divide-y divide-white/[0.06] ">
                                {visibleSections.map((section) => {
                                    const isExpanded = expandedSections.has(section.key);
                                    const tokens = getTokensForSection(section.key);
                                    const subtitle = getSectionSubtitle(section.key);
                                    return (
                                        <div key={section.key}>
                                            <SectionHeader
                                                section={section}
                                                tokens={tokens}
                                                isExpanded={isExpanded}
                                                onToggle={() => toggleSection(section.key)}
                                                subtitle={subtitle}
                                            />
                                            {isExpanded && renderSectionContent(section.key)}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-pai-subtle flex justify-between items-center">
                    <div className="text-xs text-pai-meta">
                        {t('msgCtxFooterNote', 'This is the exact prompt and context sent to the LLM for this response.')}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 text-sm transition-colors"
                    >
                        {t('commonClose', 'Close')}
                    </button>
                </div>
            </Modal>
    );
};

export default ContextViewerModal;
